import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core'
import { Permission } from '@employer/app/config/permission.config'
import { IJobPost } from '@employer/app/models/job-post.model'
import { CandidateProfileShareModalComponent } from '@employer/app/modules/jobs/components/candidate-share-profile/candidate-profile-share-modal.component'
import { JobApplicationViewsStore } from '@employer/app/modules/jobs/stores/job-application-views.store'
import { JobApplicationStore } from '@employer/app/modules/jobs/stores/job-application.store'
import { CandidateDetailTabs, CandidateNavigationSource, EmployerNavigationService } from '@employer/app/services/navigation.service'
import { PermissionStore } from '@engineering11/access-web'
import { Timestamp } from '@engineering11/types'
import { isDeepEqual, isNotNil } from '@engineering11/utility'
import { Observable, combineLatest, distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs'
import {
  APPLICATION_STATE,
  ARCHIVED_STATES,
  CnectFeatures,
  ConfigStore,
  CultureIndexPermissions,
  IJobPostApplication,
  ISurveyMatchResult,
} from 'shared-lib'

export enum CandidateActions {
  Preview = 'Preview',
  ViewDetails = 'View details',
  Feedback = 'Feedback',
  SendMessage = 'Send message',
  RequestInfo = 'Request info',
  Dialogue = 'Interview Session',
  Reactivate = 'Reactivate',
  ShareProfile = 'Share Profile',
  ProtectCandidate = 'Protect Candidate',
  RemoveProtection = 'Remove Protection',
  Reject = 'Reject',
}

const actionToTab: { [key in string]: CandidateDetailTabs } = {
  [CandidateActions.ViewDetails]: CandidateDetailTabs.Application,
  [CandidateActions.Feedback]: CandidateDetailTabs.Feedback,
  [CandidateActions.RequestInfo]: CandidateDetailTabs.RequestMoreInfo,
  [CandidateActions.SendMessage]: CandidateDetailTabs.DirectMessage,
  [CandidateActions.Dialogue]: CandidateDetailTabs.Dialogues,
}

@Component({
  selector: 'ui-alg-card',
  templateUrl: './ui-alg-card.component.html',
  styleUrls: ['./ui-alg-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiAlgCardComponent implements OnInit, AfterViewInit {
  @ViewChild('modalProfileShare') modalProfileShare!: CandidateProfileShareModalComponent

  @Input() item?: Timestamp<IJobPostApplication> // Item comes from Algolia, so might not PERFECTLY reflect IJobPostApplication

  @Input() loading: boolean = false
  @Input() viewAll: boolean = false // Non swimlane view. All candidates are being viewed so we will use this to show candidate status (since the swimlane wont do that for us)
  @Input() selectedJob!: IJobPost
  @Input() isProtected?: boolean
  @Input() showProtection?: boolean
  @Input() surveyMatchResult?: ISurveyMatchResult | null
  @Input() surveyLoading: boolean = false
  @Output() candidateSelected = new EventEmitter<string>()
  @Output() changeApplicationStatusClicked = new EventEmitter<APPLICATION_STATE>()
  @Output() reactivateApplicationClicked = new EventEmitter()
  @Output() onRemoveProtection = new EventEmitter()
  @Output() onAddProtection = new EventEmitter()

  candidateActions = CandidateActions

  protected readonly APPLICATION_STATE = APPLICATION_STATE

  applicationStatusOverflowOptions = [
    APPLICATION_STATE.APPLIED,
    APPLICATION_STATE.SCREENED,
    APPLICATION_STATE.INTERVIEWING,
    APPLICATION_STATE.FINALIST,
    APPLICATION_STATE.OFFER_SENT,
    APPLICATION_STATE.HIRED,
    APPLICATION_STATE.UNRESPONSIVE,
    APPLICATION_STATE.NOT_INTERESTED,
  ]

  applicationViewed$?: Observable<boolean>
  lastApplicationViewed$: Observable<string | null> = this.jobApplicationViewsStore.lastApplicationViewed$
  isUpdatingApplication$?: Observable<boolean>

  features$: Observable<CnectFeatures | undefined> = this.configStore.features$
  canManageApplicationShares$ = this.permissionStore.hasPermission(Permission.ManageApplicationShares)
  canManageCultureIndex$ = combineLatest([this.features$, this.permissionStore.hasPermission(CultureIndexPermissions.ManageCultureIndex)]).pipe(
    map(([features, hasPermission]) => features?.cultureIndex && hasPermission)
  )

  actionOverflowOptions$ = this.features$.pipe(
    filter(isNotNil),
    distinctUntilChanged(isDeepEqual),
    withLatestFrom(this.canManageApplicationShares$.pipe(filter(isNotNil))),
    map(([features, canManageApplicationShares]) => this.getOverflowOptions(features, canManageApplicationShares))
  )

  constructor(
    private navigationService: EmployerNavigationService,
    private jobApplicationViewsStore: JobApplicationViewsStore,
    private jobApplicationStore: JobApplicationStore,
    private configStore: ConfigStore,
    private permissionStore: PermissionStore,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (!this.item) return
    this.applicationViewed$ = this.jobApplicationViewsStore.applicationViewedForJob$(this.item.jobPostId, this.item.id)
    this.isUpdatingApplication$ = this.jobApplicationStore.isUpdatingApplicationStatus$(this.item.id)
  }

  ngAfterViewInit() {
    this.cdRef.detectChanges()
  }

  candidateSelection() {
    this.candidateSelected.emit(this.item?.id)
  }

  candidateActionOverflowChoice(event: CandidateActions): CandidateActions {
    if (!this.item) {
      return event
    }

    switch (event) {
      case CandidateActions.Preview: {
        this.candidateSelected.emit(this.item.id)
        return event // Type checking to ensure switch is exhaustive
      }
      case CandidateActions.Reject: {
        this.changeApplicationStatusClicked.emit(APPLICATION_STATE.NOT_INTERESTED)
        return event
      }
      case CandidateActions.Reactivate: {
        this.reactivateApplicationClicked.emit()
        return event
      }
      case CandidateActions.ShareProfile: {
        this.modalProfileShare.openModal()
        return event
      }
      case CandidateActions.ViewDetails:
      case CandidateActions.SendMessage:
      case CandidateActions.RequestInfo:
      case CandidateActions.Feedback:
      case CandidateActions.Dialogue: {
        this.navigationService.setCandidateNavSource(CandidateNavigationSource.List)
        this.navigationService.toCandidateDetailPage(this.item.jobPostId, this.item.id, actionToTab[event])
        return event
      }
      case CandidateActions.ProtectCandidate: {
        this.onAddProtection.emit()
        return event
      }
      case CandidateActions.RemoveProtection: {
        this.onRemoveProtection.emit()
        return event
      }
    }
  }

  changeApplicationStatus(status: APPLICATION_STATE) {
    this.changeApplicationStatusClicked.emit(status)
  }

  private getOverflowOptions(features: CnectFeatures, canManageApplicationShares: boolean) {
    const candidateActionOverflowOptions = [CandidateActions.Preview, CandidateActions.ViewDetails, CandidateActions.Feedback]
    const candidateActiveOptions = [CandidateActions.SendMessage, CandidateActions.RequestInfo, CandidateActions.Dialogue]

    if (!ARCHIVED_STATES.includes(this.item?.applicationState!)) candidateActionOverflowOptions.push(...candidateActiveOptions)
    if (canManageApplicationShares && features?.employerShareApplication) candidateActionOverflowOptions.push(CandidateActions.ShareProfile)
    return candidateActionOverflowOptions
  }

  onAnchorClick(event: MouseEvent) {
    event.stopPropagation()
  }
}
